import { formatPrice } from '@/shared/lib/format'
import { getCakePrice, CAKE_WEIGHT } from '@/shared/lib/pricing'
import { FavoriteButton } from '@/features/favorites/ui'
import type { Tea, Teaware } from '@/entities/product'

export interface ProductIdentityBlockProps {
  /** Товар (чай или посуда) */
  product: Tea | Teaware
  /** Цена за 1 г для чая (для расчетов) или полная цена для посуды */
  price: number
  /** Тип товара */
  productType?: 'tea' | 'teaware'
  /** Только блин (без вариантов 10/50/100г) */
  isCakeOnly?: boolean
  /** Цена за блин */
  cakePriceAmount?: number | null
  /** Вес блина */
  cakeWeight?: number | null
}

/**
 * Виджет: Базовый информационный блок товара
 * 
 * Отображает: название, категорию, цену, кнопку избранного
 */
export function ProductIdentityBlock({
  product,
  price,
  productType = 'tea',
  isCakeOnly,
  cakePriceAmount,
  cakeWeight,
}: ProductIdentityBlockProps) {
  const isTea = productType === 'tea'
  
  // Категория товара
  const categoryName = product.categoryName || (isTea ? 'Чай' : 'Посуда')
  
  return (
    <div className="px-4 py-6 space-y-4">
      {/* Название и избранное */}
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1 min-w-0">
          <h1 className="text-2xl font-medium text-ink dark:text-white mb-1 leading-tight">
            {product.name}
          </h1>
          {/* Артикул - только для посуды */}
          {!isTea && 'sku' in product && product.sku && (
            <p className="text-xs font-mono text-gray-400 dark:text-gray-500 mb-1">
              Арт. {product.sku}
            </p>
          )}
          <p className="text-base text-gray-600 dark:text-gray-400">
            {categoryName}
          </p>
        </div>
        
        {/* Кнопка избранного */}
        <FavoriteButton
          productId={product.id}
          productType={productType}
          variant="default"
          size="lg"
          animated
        />
      </div>
      
      {/* Цена */}
      {price > 0 ? (
        <div className="flex items-baseline gap-2 flex-wrap">
          <span className="text-3xl font-bold text-primary dark:text-primary-light">
            {formatPrice(
              isTea
                ? isCakeOnly
                  ? getCakePrice(price, cakePriceAmount)
                  : price * 10
                : price
            )}
          </span>
          {isTea && (
            <span className="text-base text-gray-500 dark:text-gray-400">
              {isCakeOnly ? `за блин (${cakeWeight ?? CAKE_WEIGHT}г)` : 'за 10г'}
            </span>
          )}
        </div>
      ) : (
        <div className="flex items-baseline gap-2 flex-wrap">
          <span className="text-xl font-medium text-gray-500 dark:text-gray-400">
            Цена по запросу
          </span>
        </div>
      )}
    </div>
  )
}
